import { useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ApiClientError } from '../lib/api';
import { useAuth } from '../lib/useAuth';

const input =
  'rounded-md border border-slate-300 px-3 py-2 text-slate-800 focus:border-amber-500 focus:outline-none';

export function AdminLoginPage() {
  const { login, status, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation() as { state?: { from?: string } };
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (status === 'authenticated' && isAdmin) {
    return <Navigate to={location.state?.from ?? '/admin'} replace />;
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await login(email, password);
      navigate(location.state?.from ?? '/admin', { replace: true });
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Unable to sign in. Try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="flex min-h-screen items-center justify-center bg-slate-100 px-4"
      style={{ colorScheme: 'light' }}
    >
      <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-lg">
        <div className="mb-6 flex items-center gap-3">
          <span
            className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-slate-950 font-display text-xl text-amber-400"
            aria-hidden
          >
            BS
          </span>
          <div>
            <h1 className="font-sans text-lg font-semibold tracking-normal text-slate-900">
              Admin sign in
            </h1>
            <p className="text-xs text-slate-500">BrandonStrong.com reservations</p>
          </div>
        </div>

        <form onSubmit={submit} className="flex flex-col gap-4">
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium text-slate-700">Email</span>
            <input
              type="email"
              required
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={input}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium text-slate-700">Password</span>
            <input
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={input}
            />
          </label>
          {error && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
          <button
            type="submit"
            disabled={busy}
            className="rounded-md bg-slate-950 px-4 py-2 text-sm font-medium text-amber-400 hover:bg-slate-800 disabled:opacity-60"
          >
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
        <p className="mt-4 text-center text-xs text-slate-400">
          Accounts are created by the site administrator.
        </p>
      </div>
    </div>
  );
}
